import { Component, Inject, HostListener, ViewChild } from '@angular/core';
import { FormControl, NgForm } from '@angular/forms';
import { OnInit } from '@angular/core';
import { Angulartics2 } from 'angulartics2';
import { OrderSettings } from 'app/orders/settings/settings.model';
import { OrderSettingsDcItemModel } from 'app/orders/settings-dc/settings-dc.model';
import { UIDialogService } from 'app/ui/dialog/dialog.service';
import { UserSettingsService } from 'app/user/user-settings.service';
import { WizardService } from 'app/orders/wizard/wizard.service';
import { WizardUploadComponent } from 'app/orders/wizard/upload/upload.component';
import { UploadSettings } from 'app/orders/wizard/upload/upload-settings.model';
import { UIDialogData } from 'app/ui/dialog/dialog-injector';
import { camelCase } from 'app/core/case-transform';
import { PersonalizedLocalStorageService } from 'app/notifications/personalized-local-storage.service';
import { ApplicationsService } from 'app/applications/applications.service';

@Component({
  selector: 'wizard-create',
  templateUrl: 'create.component.html',
  styleUrls: ['create.component.scss']
})
export class WizardCreateComponent implements OnInit {

  orderSettings: OrderSettings;
  orderSettingsDc: OrderSettingsDcItemModel[];
  rememberImportSettings: boolean;
  error: string;
  errorDescription: string;
  isLoading: boolean = false;
  private templateError: string = 'Error';
  private templateErrorDescription: string = 'Error of processing of parameters';

  get visibleRegion(): boolean {
    return (this._userSettingsService.serviceType === 'ic');
  }

  get isFilesReady() {
    return this._settings.isFilesReady;
  }

  @ViewChild('settings') form: NgForm;

  constructor(@Inject(UIDialogData)
    private _settings: UploadSettings,
    private _angulartics2: Angulartics2,
    private _dialogService: UIDialogService,
    private _userSettingsService: UserSettingsService,
    private _wizardService: WizardService,
    private _applicationsService: ApplicationsService,
    private _personalizedLocalStorageService: PersonalizedLocalStorageService) {
  }

  ngOnInit() {
    this.orderSettingsDc = [];
    this.orderSettings = Object.assign({}, this._userSettingsService.orderSettings);
    this.rememberImportSettings = this._userSettingsService.rememberImportSettings;
  }

  back() {
    this._saveSettings();
    this._dialogService.open(WizardUploadComponent, { data: this._settings });
  }

  canStartProcess(form: NgForm) {
    return !this.isLoading && form.valid && this.isFilesReady;
  }

  onSubmit(form: NgForm) {
    if (form.valid && this._isValidOrderSettings()) {
      this.onHideError();
      this.isLoading = true;
      this._saveSettings();
      let data: any = {};
      try {
        if (this.isIc()) {
          data.verification = this.orderSettings.verificationType;
          data.export_format = this.orderSettings.exportFormat;
          data.region = this.orderSettings.region;
        } else {
          // [TFS-128713]: При загрузке файла в DC не передаются регистрационные параметры
          if (this._applicationsService.defaultApplication().isCustomized) {
            // Выставляем дефолтные значения только для демо-приложений
            const _temp = this.orderSettingsDc.reduce((acc, cur) => {
              if (cur.name && cur.name !== '') {
                acc[cur.name] = cur.value || '';
              }
              return acc;
            }, {});
            Object.assign(data, _temp);
          } else {
            const _temp = this.orderSettingsDc.reduce((acc, cur) => {
              if (cur.name && cur.name !== '') {
                acc[cur.name] = cur.value || '';
              }
              return acc;
            }, {
              document_type: this._userSettingsService.documentType,
              verification: this.orderSettings.verificationType,
              export_format: this.orderSettings.exportFormat,
              region: this.orderSettings.region,
            });
            Object.assign(data, this.orderSettings);
            Object.assign(data, _temp);
          }
        }
        this._wizardService.create(data, this._userSettingsService.serviceType).subscribe(order => {
          this._personalizedLocalStorageService.setJson('order_properties', this.orderSettingsDc);
          const properties = {
            event: 'Create Order',
            'Service Type': order.serviceType,
            'Document Count': this._wizardService.files.length,
            'Export Format': this.orderSettings.exportFormat,
            'Invoice Region': this.orderSettings.region,
            'Verification Type': this.orderSettings.verificationType,
            'Number': order.number
          };
          if (!this.isIc()) {
            delete properties['Invoice Region'];
          }
          this._angulartics2.eventTrack.next({
            action: 'Create Order',
            properties: properties
          });
          this._wizardService.reset();
          this._dialogService.close();
        }, error => this._onError(error));
      } catch (error) {
        data = {};
        this.error = this.templateError;
        this.errorDescription = this.templateErrorDescription;
        console.error(error);
        this.isLoading = false;
      }
    }
  }

  isIc(): boolean {
    return (this._userSettingsService.serviceType === 'ic');
  }

  onHideError(): void {
    this.error = null;
    this.errorDescription = null;
  }

  private _onError(response): void {
    if (response.status === 400) {
      const responseJson = response.json();
      this.error = camelCase(responseJson.error);
      this.errorDescription = this._getErrorDescription(responseJson);
    } else {
      this.error = this.templateError;
      this.errorDescription = this.templateErrorDescription;
    }
    this.isLoading = false;
  }

  private _saveSettings(): void {
    this._userSettingsService.orderSettings = Object.assign({}, this.orderSettings);
    this._userSettingsService.rememberImportSettings = this.rememberImportSettings;
  }

  private _getErrorDescription(responseJson): string {
    const modelState = responseJson.modelState;
    if (!modelState) {
      return responseJson.errorDescription;
    }

    return Object.keys(modelState)
      .map(fieldName => modelState[fieldName].join('\n'))
      .join('\n');
  }

  private _isValidOrderSettings() {
    let isNoError = true;
    if (!this.isIc()) {
      this.orderSettingsDc.forEach((value, i, array) => {
        if (value.name === '' && value.value !== '') {
          array[i].errorValue = true;
          isNoError = false;
        } else {
          array[i].errorValue = false;
        }
      });
    }
    return isNoError;
  }

  setOrderSettingsDc(value: OrderSettingsDcItemModel[]) {
    this.orderSettingsDc = value;
  }

  @HostListener('document:keydown', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    if (event.keyCode === 13 && this.canStartProcess(this.form)) {
      this.onSubmit(this.form);
    }
  }
}
